import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Switch from '@mui/material/Switch';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TableSortLabel from '@mui/material/TableSortLabel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import SearchIcon from '@mui/icons-material/Search';

import { BreadcrumbHeader, FilterTabs, EmptyState, ActionMenu, ConfirmModal } from '../../components/shared';
import { customers as initialCustomers, warehouses } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Constants ────────────────────────────────────────────────────────────────
import ContentCard from "../../components/shared/ContentCard";const TYPE_TABS = [
{ label: 'All', value: 'All' },
{ label: 'End-Customer', value: 'End-Customer' },
{ label: 'Reseller', value: 'Reseller' }];

const TYPE_COLORS = {
  'End-Customer': { bg: '#E0F2F1', color: '#00695C' },
  'Reseller': { bg: '#EDE7F6', color: '#4527A0' }
};

const EMPTY_FORM = {
  name: '',
  customerType: 'End-Customer',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  active: true
};

const TH = {
  fontSize: '0.75rem', fontWeight: 700, color: 'var(--grey-600)',
  backgroundColor: '#eef2f6', py: '10px', px: 2,
  borderBottom: '1px solid var(--grey-200)', whiteSpace: 'nowrap'
};
const TD = {
  fontSize: '0.875rem', color: 'var(--grey-800)',
  py: 0, px: 2, height: 48,
  borderBottom: '1px solid var(--grey-200)'
};

// ─── Type badge ───────────────────────────────────────────────────────────────
function TypeBadge({ type }) {
  const cfg = TYPE_COLORS[type] ?? { bg: '#ECEFF1', color: '#546E7A' };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '10px', py: '3px', borderRadius: '12px', backgroundColor: cfg.bg }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: cfg.color, lineHeight: 1, whiteSpace: 'nowrap' }}>
        {type}
      </Typography>
    </Box>);

}

// ─── Customer Modal ────────────────────────────────────────────────────────────
function CustomerModal({ open, mode, initial, onClose, onSave }) {
  const [form, setForm] = useState(EMPTY_FORM);

  const handleEnter = () =>
  setForm(
    mode === 'edit' && initial ?
    {
      name: initial.name,
      customerType: initial.customerType ?? 'End-Customer',
      contactName: initial.contactName,
      contactEmail: initial.contactEmail,
      contactPhone: initial.contactPhone ?? '',
      active: initial.active
    } :
    EMPTY_FORM
  );

  const set = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  const isValid = form.name && form.contactName && form.contactEmail;

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {if (reason !== 'backdropClick') onClose();}}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}>
      
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {mode === 'edit' ? 'Edit Customer' : 'Add Customer'}
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        <TextField
          size="small" label="Customer Name" value={form.name} onChange={set('name')}
          fullWidth required sx={fieldSx} />

        {/* Customer Type */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', minWidth: 80 }}>Customer Type</Typography>
          <ToggleButtonGroup
            exclusive size="small"
            value={form.customerType}
            onChange={(_, val) => {if (val) setForm((prev) => ({ ...prev, customerType: val }));}}
            sx={{ '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, px: 2, py: '4px',
                borderColor: 'var(--grey-200)', color: 'var(--grey-500)',
                '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                '&.Mui-selected:hover': { backgroundColor: '#00796B' }
              }
            }}>
            
            <ToggleButton value="End-Customer">End-Customer</ToggleButton>
            <ToggleButton value="Reseller">Reseller</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            size="small" label="Contact Name" value={form.contactName} onChange={set('contactName')}
            fullWidth required sx={fieldSx} />
          
          <TextField
            size="small" label="Contact Email" type="email" value={form.contactEmail} onChange={set('contactEmail')}
            fullWidth required sx={fieldSx} />
          
        </Box>

        <TextField
          size="small" label="Contact Phone (optional)" value={form.contactPhone} onChange={set('contactPhone')}
          fullWidth sx={fieldSx} />

        {mode === 'edit' &&
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)' }}>Status</Typography>
            <ToggleButtonGroup
            exclusive size="small"
            value={form.active ? 'active' : 'inactive'}
            onChange={(_, val) => {if (val) setForm((prev) => ({ ...prev, active: val === 'active' }));}}
            sx={{ '& .MuiToggleButton-root': {
                textTransform: 'none', fontSize: '0.8125rem', fontWeight: 600, px: 2, py: '4px',
                borderColor: 'var(--grey-200)', color: 'var(--grey-500)',
                '&.Mui-selected': { backgroundColor: '#009688', color: '#fff', borderColor: '#009688' },
                '&.Mui-selected:hover': { backgroundColor: '#00796B' }
              }
            }}>
            
              <ToggleButton value="active">Active</ToggleButton>
              <ToggleButton value="inactive">Inactive</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}>
          
          Cancel
        </Typography>
        <Button
          variant="contained" size="small"
          disabled={!isValid}
          onClick={() => onSave(form)}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
            '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
            textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2
          }}>
          
          {mode === 'edit' ? 'Save Changes' : 'Add Customer'}
        </Button>
      </DialogActions>
    </Dialog>);

}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function CustomersPage() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState(initialCustomers);
  const [typeFilter, setTypeFilter] = useState('All');
  const [showInactive, setShowInactive] = useState(false);
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'asc' });

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [editTarget, setEditTarget] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deactTarget, setDeactTarget] = useState(null);

  const [banner, setBanner] = useState('');
  const showBanner = (msg) => {setBanner(msg);setTimeout(() => setBanner(''), 4000);};

  // Count On-Site locations per customer
  const locationCountMap = useMemo(() => {
    const map = {};
    warehouses.forEach((w) => {
      if (w.type === 'On-Site' && w.customerName) {
        map[w.customerName] = (map[w.customerName] ?? 0) + 1;
      }
    });
    return map;
  }, []);

  const handleSort = (field) =>
  setSortConfig((prev) => ({
    field,
    direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
  }));

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return [...customers]
    .filter((c) => showInactive ? !c.active : c.active)
    .filter((c) => typeFilter === 'All' || c.customerType === typeFilter)
    .filter((c) =>
    !q ||
    c.name.toLowerCase().includes(q) ||
    c.contactName.toLowerCase().includes(q) ||
    c.contactEmail.toLowerCase().includes(q)
    )
    .sort((a, b) => {
      const av = a[sortConfig.field] ?? '';
      const bv = b[sortConfig.field] ?? '';
      const cmp = String(av).localeCompare(String(bv));
      return sortConfig.direction === 'asc' ? cmp : -cmp;
    });
  }, [customers, typeFilter, showInactive, search, sortConfig]);

  const handleSave = (form) => {
    if (modalMode === 'edit') {
      setCustomers((prev) =>
      prev.map((c) => c.id === editTarget.id ? { ...c, ...form } : c)
      );
      showBanner('Customer updated successfully.');
    } else {
      const code = `CUST-${String(customers.length + 1).padStart(2, '0')}`;
      setCustomers((prev) => [
      ...prev,
      { id: `cust-new-${Date.now()}`, code, ...form }]
      );
      showBanner('Customer added successfully.');
    }
    setModalOpen(false);
  };

  const handleDeactivate = () => {
    setCustomers((prev) =>
    prev.map((c) => c.id === deactTarget.id ? { ...c, active: false } : c)
    );
    setConfirmOpen(false);
    setDeactTarget(null);
    showBanner(`${deactTarget.name} deactivated.`);
  };

  const handleActivate = (customer) => {
    setCustomers((prev) =>
    prev.map((c) => c.id === customer.id ? { ...c, active: true } : c)
    );
    showBanner(`${customer.name} activated successfully.`);
  };

  const sortDir = (field) => sortConfig.field === field ? sortConfig.direction : false;

  const COLUMNS = [
  { field: 'name', label: 'Customer', sortable: true },
  { field: '_type', label: 'Type', sortable: false },
  { field: 'contactName', label: 'Contact', sortable: true },
  { field: 'contactEmail', label: 'Email', sortable: false },
  { field: 'contactPhone', label: 'Phone', sortable: false },
  { field: '_locations', label: 'On-Site Locations', sortable: false },
  { field: '_actions', label: '', sortable: false }];

  return (
    <Box sx={{ pt: 0, px: 3, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Success banner */}
      {banner &&
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', backgroundColor: '#E8F5E9', border: '1px solid #A5D6A7', borderRadius: '8px', px: 2, py: '12px' }}>
          <CheckCircleIcon sx={{ color: '#2E7D32', fontSize: 20 }} />
          <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 600 }}>{banner}</Typography>
        </Box>
      }

      <BreadcrumbHeader
        title="Customers"
        crumbs={[{ label: 'Settings' }, { label: 'Customers' }]} />

      <ContentCard sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <FilterTabs tabs={TYPE_TABS} value={typeFilter} onChange={setTypeFilter} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search by Name, Contact, or Email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment:
              <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: 'var(--grey-400)' }} />
                </InputAdornment>

            }}
            sx={{ minWidth: 400, maxWidth: 600, backgroundColor: 'var(--paper)', borderRadius: 1, ...fieldSx }} />

          <Box sx={{ flex: 1 }} />

          {/* Show Inactive toggle */}
          <Box
            sx={{ display: 'flex', alignItems: 'center', gap: '4px', cursor: 'pointer' }}
            onClick={() => setShowInactive((prev) => !prev)}>
            
            <Switch
              checked={showInactive}
              size="small"
              onChange={(e) => setShowInactive(e.target.checked)}
              sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#009688' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#009688' } }} />
            
            <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-600)', userSelect: 'none' }}>
              Show Inactive
            </Typography>
          </Box>

          {/* Add Customer button */}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => {setModalMode('add');setEditTarget(null);setModalOpen(true);}}
            sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' },
              textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none'
            }}>
            
            Add Customer
          </Button>
        </Box>

        <Box sx={{ borderRadius: '8px', border: '1px solid var(--grey-200)', overflow: 'hidden' }}>
          {filtered.length === 0 ?
          <EmptyState
            title="No customers found"
            description={showInactive ? 'No inactive customers.' : 'Try adjusting your search or add a new customer.'} /> :

          <Table size="small">
              <TableHead>
                <TableRow>
                  {COLUMNS.map((col) =>
                <TableCell key={col.field} sx={{ ...TH, width: col.width }}>
                      {col.sortable ?
                  <TableSortLabel
                    active={sortConfig.field === col.field}
                    direction={sortDir(col.field) || 'asc'}
                    onClick={() => handleSort(col.field)}
                    sx={{ '&.Mui-active': { color: '#009688' }, '& .MuiTableSortLabel-icon': { color: '#009688 !important' } }}>
                    
                          {col.label}
                        </TableSortLabel> :
                  col.label}
                    </TableCell>
                )}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((customer) => {
                const locationCount = locationCountMap[customer.name] ?? 0;
                const actions = [
                { label: 'Edit', onClick: () => {setModalMode('edit');setEditTarget(customer);setModalOpen(true);} },
                { label: 'View On-Site Locations', onClick: () => navigate('/inventory/search?tab=locations', { state: { customerFilter: customer.name } }) },
                customer.active ?
                { label: 'Deactivate', onClick: () => {setDeactTarget(customer);setConfirmOpen(true);} } :
                { label: 'Activate', onClick: () => handleActivate(customer) }];

                return (
                  <TableRow key={customer.id} hover sx={{ cursor: 'default' }}>
                      <TableCell sx={TD}>
                        <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>
                          {customer.name}
                        </Typography>
                        <Typography sx={{ fontSize: '0.7rem', color: 'var(--grey-400)' }}>
                          {customer.code}
                        </Typography>
                      </TableCell>
                      <TableCell sx={TD}>
                        <TypeBadge type={customer.customerType ?? 'End-Customer'} />
                      </TableCell>
                      <TableCell sx={TD}>{customer.contactName}</TableCell>
                      <TableCell sx={TD}>
                        <Typography
                        component="a"
                        href={`mailto:${customer.contactEmail}`}
                        sx={{ fontSize: '0.875rem', color: '#009688', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                        
                          {customer.contactEmail}
                        </Typography>
                      </TableCell>
                      <TableCell sx={TD}>{customer.contactPhone || '—'}</TableCell>
                      <TableCell sx={TD}>
                        <Typography sx={{ fontSize: '0.875rem', color: locationCount > 0 ? 'var(--grey-800)' : 'var(--grey-400)' }}>
                          {locationCount > 0 ? locationCount : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ ...TD, width: 56, textAlign: 'right' }}>
                        <ActionMenu actions={actions} hideUntilRowHover />
                      </TableCell>
                    </TableRow>);

              })}
              </TableBody>
            </Table>
          }
        </Box>
      </ContentCard>

      <CustomerModal
        open={modalOpen}
        mode={modalMode}
        initial={editTarget}
        onClose={() => setModalOpen(false)}
        onSave={handleSave} />

      <ConfirmModal
        open={confirmOpen}
        title="Deactivate Customer"
        message={`Are you sure you want to deactivate ${deactTarget?.name}? They will no longer appear in active customer lists.`}
        confirmLabel="Deactivate"
        onConfirm={handleDeactivate}
        onClose={() => {setConfirmOpen(false);setDeactTarget(null);}} />
      
    </Box>);

}